import React from "react";
import { Card, Col, Row, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { Badge } from "react-bootstrap";
import loader from "assets/images/datahub-loader.svg";
import ElectricityIcon from '@enerex/unified-ui-react/icons/ElectricityIcon';
import GasIcon from '@enerex/unified-ui-react/icons/GasIcon';
import BlurSpan from "../../../components/BlurSpan";
import { trimInvalidDate, trimInvalidUTCDate } from './../../../helpers/api/utils';
import { titleConvert } from "./../../../helpers/api/utils";

const electricityIconStyle = { color: "#ffc107", width: 18, height: 18 };
const gasIconStyle = { color: "#0acf97", width: 18, height: 18 };

// Utility: safe date parsing & filtering
const safeDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (
      isNaN(d) ||
      dateStr.startsWith("0000") ||
      dateStr.startsWith("0001")
    ) {
      return "";
    }
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

const commodityIcon = (commodity) => {
  const c = (commodity || "").toLowerCase();
  if (c === "electric" || c === "electricity" || c === "1") {
    return <ElectricityIcon style={electricityIconStyle} />;
  }
  if (c === "gas" || c === "2") {
    return <GasIcon style={gasIconStyle} />;
  }
  return "-";
};

const LicenseList = ({ licenses = [] }) => {
  if (!licenses || licenses.length === 0) {
    return <div className="placeholder-box">No licenses found.</div>;
  }

  const columns = [
    {
      name: "",
      selector: (row) => row.commodity,
      cell: (row) => commodityIcon(row.commodity),
      width: '10px'
      // maxWidth: "10px",
    },
    {
      name: "State",
      selector: (row) => row.stateCode,
      sortable: true,
      width: "80px",
    },
    {
      name: "Legal Name",
      selector: (row) => titleConvert(row.associatedLegalName??''),
      cell : (row) =>  <span className="ellipsis" title={row.associatedLegalName}>{row.associatedLegalName}</span>,
      // width: "250px",
      sortable: true,
      grow: 3,
    },
    {
      name: "Type",
      selector: (row) => row.licenseType,
      sortable: true,
      // width: "140px",
      grow:1,
    },
    {
      name: "License Number",
      selector: (row) => row.licenseNumber,
      cell: (row) => {
            const isActive = row.licenseStatusIsActive;
            const normalizedStatus = row.normalisedLicenseStatus;
            
            // Don't show if isActive is null or normalizedStatus is null/empty
            // if (isActive === null || isActive === undefined || !normalizedStatus) {
            //     return <BlurSpan isBlur={!row.isPremium}></BlurSpan>;
            // }
            
            // green for active (true), red for inactive (false)
            const dotColor = isActive === true ? "#28a745" : "#dc3545";
            const activeStatus = isActive === true ? "Active" : "Inactive";
            
            return (
                <BlurSpan isBlur={!row.isPremium}>
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 250, hide: 100 }}
                        overlay={ isActive != null && isActive !== undefined && normalizedStatus ? 
                            <Tooltip id={`license-status-tooltip-${row.id || row.licenseId || Math.random()}`}>
                                {normalizedStatus}
                            </Tooltip>
                            : <></>
                        }
                    >
                        <span 
                            style={{ 
                                display: "inline-flex", 
                                alignItems: "center", 
                                gap: "6px", 
                                cursor: "help"
                            }}
                        >
                            {
                              isActive!=null ? <span
                                style={{
                                    width: "8px",
                                    height: "8px",
                                    borderRadius: "50%",
                                    backgroundColor: dotColor,
                                    display: "inline-block",
                                    flexShrink: 0
                                }} />: <span
                                style={{
                                    width: "8px",
                                    height: "8px",
                                    
                                }}
                            />
                            
                            }
                            
                            <span>{row.licenseNumber}</span>
                        </span>
                    </OverlayTrigger>
                </BlurSpan>
            );
        },
        grow:1.4
      // width: "120px",
    },
    {
      name: "Start Date",
      selector: (row) => {
        const licenseRegisteredDate = trimInvalidUTCDate(row?.licenseRegisteredDate?.split("T")[0]) || trimInvalidUTCDate(row?.licenseRegisteredDate?.split("T")[0]);
        const isLatest = row?.isLatest || false;
        return <span style={!isLatest?{color:'red'}:{}}>{licenseRegisteredDate}</span>;
      },
      grow:1,
      // width: "120px",
    },
    {
      name: "End Date",
      selector: (row) => {
        const licenseExpiryDate = trimInvalidUTCDate(row?.licenseExpiryDate?.split("T")[0]) || trimInvalidUTCDate(row?.licenseExpiryDate?.split("T")[0]);
        const isLatest = row?.isLatest || false;
        return <span style={!isLatest?{color:'red'}:{}}>{licenseExpiryDate}</span>;
      },
      grow:1,
      // width: "120px",
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#eef2f7",
        minHeight: "32px",
      },
    },
    headCells: {
      style: {
        fontSize: "12px",
        fontWeight: 600,
        color: "#474d56",
      },
    },
    rows: {
      style: {
        minHeight: "32px",
        fontSize: "12px",
      },
    },
    // cells: {
    //   style: {
    //     paddingLeft: "6px",
    //     paddingRight: "6px",
    //   },
    // },
  };

  return (
    <DataTable
      columns={columns}
      data={licenses}
      customStyles={customStyles}
      dense
      highlightOnHover
      fixedHeader          // ✅ keeps header fixed
      fixedHeaderScrollHeight="60vh" // ✅ scroll height
      noHeader={false}     // ✅ now header is visible
      progressPending={!licenses}
      progressComponent={
        <div className="d-flex justify-content-center p-3">
          <img src={loader} alt="loading" height="40" />
        </div>
      }
    />
  );
};

export default LicenseList;
